import { useState } from "react";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { MessagesView } from "./views/MessagesView";
import { MessageLogView } from "./views/MessageLogView";
import { ContactsView } from "./views/ContactsView";
import { SettingsView } from "./views/SettingsView";
import { ComposeModal } from "./components/ComposeModal";

type LineFilter = "all" | "line1" | "line2";

const LINES: { key: LineFilter; label: string }[] = [
  { key: "all", label: "All Lines" },
  { key: "line1", label: "Line 1" },
  { key: "line2", label: "Line 2" },
];

const SUB_TABS = [
  { to: "", label: "Messages", match: ["/sms", "/sms/"] },
  { to: "log", label: "Log", match: ["/sms/log"] },
  { to: "contacts", label: "Contacts", match: ["/sms/contacts"] },
  { to: "settings", label: "Settings", match: ["/sms/settings"] },
] as const;

export function SmsModule() {
  const { me } = useAuth();
  const isAdmin = !!me?.is_admin;
  const [lineFilter, setLineFilter] = useState<LineFilter>("all");
  const [composeOpen, setComposeOpen] = useState(false);
  const [messageRefreshKey, setMessageRefreshKey] = useState(0);
  const [contactRefreshKey, setContactRefreshKey] = useState(0);

  const location = useLocation();
  const showLineFilter =
    location.pathname === "/sms" ||
    location.pathname === "/sms/" ||
    location.pathname.startsWith("/sms/log");

  const phoneLine = lineFilter === "all" ? null : lineFilter;

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-tribal-border bg-tribal-surface px-4 py-3">
        <div className="flex min-w-0 items-center gap-4">
          <h1 className="text-lg font-semibold tracking-tight">
            <span className="text-tribal-red">Tribal Axe</span> SMS
          </h1>
          <nav
            aria-label="SMS views"
            className="hidden items-center gap-0.5 rounded-lg bg-black/30 p-0.5 sm:flex"
          >
            {SUB_TABS.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.to === ""}
                className={({ isActive }) =>
                  `rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-tribal-border text-white"
                      : "text-tribal-muted hover:text-white"
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          {showLineFilter && (
            <>
              <div className="hidden items-center gap-0.5 rounded-lg bg-black/30 p-0.5 sm:flex">
                {LINES.map((line) => (
                  <button
                    key={line.key}
                    type="button"
                    onClick={() => setLineFilter(line.key)}
                    className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                      lineFilter === line.key
                        ? "bg-tribal-red text-white"
                        : "text-tribal-muted hover:text-white"
                    }`}
                  >
                    {line.label}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setComposeOpen(true)}
                className="rounded-lg bg-tribal-red px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark"
              >
                Compose
              </button>
            </>
          )}
        </div>
      </header>

      <nav
        aria-label="SMS views (mobile)"
        className="flex flex-shrink-0 overflow-x-auto border-b border-tribal-border bg-tribal-surface sm:hidden"
      >
        {SUB_TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === ""}
            className={({ isActive }) =>
              `flex-1 whitespace-nowrap py-2 text-center text-sm font-medium transition-colors ${
                isActive
                  ? "border-b-2 border-tribal-red text-tribal-red"
                  : "text-tribal-muted"
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {showLineFilter && (
        <div className="flex flex-shrink-0 gap-1 overflow-x-auto border-b border-tribal-border bg-tribal-bg px-2 py-1.5 sm:hidden">
          {LINES.map((line) => (
            <button
              key={line.key}
              type="button"
              onClick={() => setLineFilter(line.key)}
              className={`whitespace-nowrap rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                lineFilter === line.key
                  ? "bg-tribal-red text-white"
                  : "bg-tribal-surface text-tribal-muted"
              }`}
            >
              {line.label}
            </button>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-hidden">
        <Routes>
          <Route
            index
            element={
              <MessagesView
                phoneLine={phoneLine}
                refreshKey={messageRefreshKey}
                onMessageSent={() => setMessageRefreshKey((k) => k + 1)}
                onContactAdded={() => setContactRefreshKey((k) => k + 1)}
                contactRefreshKey={contactRefreshKey}
              />
            }
          />
          <Route path="log" element={<MessageLogView phoneLine={phoneLine} />} />
          <Route
            path="contacts"
            element={
              <ContactsView
                isAdmin={isAdmin}
                refreshKey={contactRefreshKey}
                onChange={() => setContactRefreshKey((k) => k + 1)}
              />
            }
          />
          <Route path="settings" element={<SettingsView isAdmin={isAdmin} />} />
          <Route path="*" element={<Navigate to="." replace />} />
        </Routes>
      </div>

      {composeOpen && (
        <ComposeModal
          onClose={() => setComposeOpen(false)}
          onSent={() => {
            setComposeOpen(false);
            setMessageRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
